import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppContext } from "../../../context/AuthContext";
import { ArticleController, ArticleControllerType } from "../../../controller/article/ArticleController";
import { handleErrorMessage, useEffectOnce } from "../../../utils/Utils";

interface OverrideArticleServiceType {
  article: any;
  getArticleById: Function;
  setReviewersCount: Function;
  numberArray: number[];
  active: number | undefined;
  setActive: any;
  AddReviewer: Function;
  DeleteReviewer: Function;
  getMailSuggestionToReviewer: Function;
  addedReviewers: any;
  reviewers: any;
  sendMailToAllReviewers: any;
  sendMailToReviewer: any;
  handleOpenDialog: any;
  handleCloseDialog: any;
  openDialog: any;
  mailSuggestion: any;
}

export default function OverrideArticleService(): OverrideArticleServiceType {
  const { showMessage, authData } = useAppContext();
  const { id }: any = useParams();
  const {
    axiosGetArticleById,
    axiosSetReviewersCount,
    axiosGetAddedReviewerList,
    axiosAddReviewer,
    axiosDeleteReviewer,
    axiosGetMailSuggestionToReviewer,
    axiosSendMailToAllReviewers,
    axiosGetReviewersStatistics,
    axiosSendMailToReviewer,
  }: ArticleControllerType = ArticleController();
  const [article, setArticle] = useState<any>({});
  const [mailSuggestion, setMailSuggestion] = useState<any>(null);
  const [reviewers, setReviewers] = useState<any>([]);
  const [addedReviewers, setAddedReviewers] = useState<any>([]);
  const numberArray = [0, 1, 2, 3, 4];
  const [active, setActive] = useState<number>(-1);
  const [openDialog, setOpenDialog] = useState(false);
  const mailToReviewerId = useRef(null);

  useEffectOnce(() => {
    getArticleById(id);
    getAddedReviewerList(id);
  });

  const handleOpenDialog = (reviewerId: any) => {
    mailToReviewerId.current = reviewerId;
    getMailSuggestionToReviewer(reviewerId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    mailToReviewerId.current = null;
    setMailSuggestion(null);
    setOpenDialog(false);
  };

  const AddReviewer = (reviewerId: number) => {
    axiosAddReviewer(id, reviewerId)
      .then((result: any) => {
        getAddedReviewerList(id);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const DeleteReviewer = (reviewerId: number) => {
    axiosDeleteReviewer(id, reviewerId)
      .then((result: any) => {
        getAddedReviewerList(id);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const getArticleById = (id: number) => {
    axiosGetArticleById(id)
      .then((result: any) => {
        setActive(result.data?.reviewerRequired);
        setArticle(result.data);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const getAddedReviewerList = (id: number) => {
    axiosGetAddedReviewerList(id)
      .then((result: any) => {
        const mapData = result.data.map((x: any) => ({
          ...x,
          fullName: x.reviewer.firstName + " " + x.reviewer.lastName,
        }));
        setAddedReviewers(mapData);
        getReviewersStatistics(mapData);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const getReviewersStatistics = (added: any) => {
    axiosGetReviewersStatistics()
      .then((result: any) => {
        const mapData = result.data.map((x: any) => ({
          ...x,
          fullName: x.firstName + " " + x.lastName,
        }));
        const filterData = mapData.filter((d: any) => !added.some((a: any) => d.fullName === a.reviewer.fullName));
        setReviewers(filterData);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const setReviewersCount = (count: number) => {
    axiosSetReviewersCount(id, count)
      .then((result: any) => {
        setArticle(result.data);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const getMailSuggestionToReviewer = (reviewId: number) => {
    axiosGetMailSuggestionToReviewer(id, reviewId)
      .then((result: any) => {
        setMailSuggestion(result.data);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const sendMailToAllReviewers = () => {
    axiosSendMailToAllReviewers(id)
      .then((result: any) => getAddedReviewerList(id))
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const sendMailToReviewer = (email: any) => {
    const emailDto = {
      from: authData?.user.email,
      replyTo: authData?.user.email,
      to: email.mailto.length > 0 ? email.mailto.join() : null,
      cc: email.cc.length > 0 ? email.cc.join() : null,
      sentDate: new Date(),
      subject: email.subject,
      text: email.message,
    };
    axiosSendMailToReviewer(id, mailToReviewerId.current, emailDto)
      .then((result: any) => {
        handleCloseDialog();
        getAddedReviewerList(id);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  return {
    article,
    getArticleById,
    setReviewersCount,
    numberArray,
    active,
    setActive,
    AddReviewer,
    DeleteReviewer,
    getMailSuggestionToReviewer,
    addedReviewers,
    reviewers,
    sendMailToAllReviewers,
    sendMailToReviewer,
    handleOpenDialog,
    handleCloseDialog,
    openDialog,
    mailSuggestion,
  };
}
export type { OverrideArticleServiceType };
